import { Dialog } from "@headlessui/react";
import { ArrowLeftIcon, RotateCcw, XIcon } from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

import { ModuleCommandEnum } from "@/api/events/modules/command.enum";
import {
  ApplicationError,
  MachineError,
} from "@/api/events/modules/module-tracking";
import { useSendCommand } from "@/api/modules/mutations";
import { Transition } from "@/components/Transition";

import ErrorModuleItem from "./error-module-item";

const BottomSheetErrorList = ({
  areaId,
  open,
  setOpen,
  errors,
  moduleAddress,
}: {
  areaId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  errors: (MachineError | ApplicationError)[];
  moduleAddress: string;
}) => {
  const { t: translator } = useTranslation();
  const { mutate: sendCommand, isPending } = useSendCommand({
    areaId,
  });

  return (
    <Transition key="areaDetails" show={open} as={Fragment}>
      <Dialog as="div" tw="relative z-10" onClose={() => setOpen(false)}>
        {/* Backdrop */}
        <Transition.Child
          as={"div"}
          aria-hidden="true"
          enter={tw`ease-out duration-300`}
          enterFrom={tw`opacity-0`}
          enterTo={tw`opacity-100`}
          leave={tw`ease-in duration-200`}
          leaveFrom={tw`opacity-100`}
          leaveTo={tw`opacity-0`}
          tw="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        />

        {/* Panel */}
        <div tw="fixed inset-0 z-10 flex items-end justify-center">
          <Transition.Child
            as={Dialog.Panel}
            enter={tw`ease-out duration-300`}
            enterFrom={tw`opacity-0 translate-y-full`}
            enterTo={tw`opacity-100 translate-y-0`}
            leave={tw`ease-in duration-200`}
            leaveFrom={tw`opacity-100 translate-y-0`}
            leaveTo={tw`opacity-0 translate-y-full`}
            tw="z-10 w-[95%] max-w-[775px] max-h-[90vh] bg-white text-black rounded-t-2xl overflow-hidden shadow-xl flex flex-col"
          >
            <div tw="flex w-full shrink-0 justify-between items-center text-stone-600 p-6 mb-4 border-b-2 border-gray-200 pb-4">
              <ArrowLeftIcon
                tw="h-6 w-auto cursor-pointer"
                onClick={() => setOpen(false)}
              />
              <p tw="text-2xl font-medium">Test modules erreurs</p>
              <XIcon
                tw="h-6 w-auto cursor-pointer"
                onClick={() => setOpen(false)}
              />
            </div>

            <div tw="flex flex-col gap-6 px-6 pb-6">
              <ul tw="flex flex-col gap-3">
                {errors.map((error: MachineError | ApplicationError) => {
                  return (
                    <li
                      tw="text-sm font-medium"
                      key={`${moduleAddress}-${error.code}`}
                    >
                      <ErrorModuleItem
                        moduleAddress={moduleAddress}
                        severity={error.severity}
                        severityCode={error.severityCode}
                        label={error.label}
                        doc={error.doc}
                      />
                    </li>
                  );
                })}
              </ul>
              <div tw="flex justify-center">
                <button
                  onClick={async () => {
                    sendCommand({
                      destinationAddress: moduleAddress,
                      command: ModuleCommandEnum.RESET_ERRORS,
                    });
                    setOpen(false);
                  }}
                  disabled={isPending}
                  css={[
                    tw`flex items-center gap-2 text-sm font-medium border border-red-500 rounded-md p-2 text-red-500 
                  active:bg-red-300 active:text-white active:border-white`,
                    tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                  ]}
                >
                  <RotateCcw tw="w-4 h-4" />
                  <span>
                    {translator("postMaintenance.actions.resetErrors")}
                  </span>
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BottomSheetErrorList;
